import { Link } from "react-router-dom";
import { allCategories, regions, years, sortOptions } from "@/lib/content";
import type { SortKey } from "@/lib/content";
import { works } from "@/mocks/works";

interface FilterSidebarProps {
  type: string;
  region: string;
  year: string;
  sort: SortKey;
  onChange: (patch: Record<string, string | undefined>) => void;
  onReset: () => void;
}

export default function FilterSidebar({ type, region, year, sort, onChange, onReset }: FilterSidebarProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2.5 flex items-center gap-1.5 font-heading text-[11px] uppercase tracking-[0.2em] text-foreground-600">
          <i className="ri-apps-2-line text-primary-500" />
          作品分类
        </h3>
        <div className="space-y-1">
          <Link
            to="/category/all"
            className={`flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-[13px] transition ${
              type === "all"
                ? "bg-primary-500 font-medium text-background-950"
                : "text-foreground-300 hover:bg-background-200"
            }`}
          >
            <span className="flex items-center gap-2">
              <i className="ri-stack-line" />
              全部作品
            </span>
            <span className={type === "all" ? "text-[11px] text-background-900" : "text-[11px] text-foreground-600"}>
              {works.length}
            </span>
          </Link>
          {allCategories.map((category) => {
            const active = type === category.key;
            return (
              <Link
                key={category.key}
                to={`/category/${category.key}`}
                className={`flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-[13px] transition ${
                  active
                    ? "bg-primary-500 font-medium text-background-950"
                    : "text-foreground-300 hover:bg-background-200"
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className={category.icon} />
                  {category.name}
                </span>
                <span className={active ? "text-[11px] text-background-900" : "text-[11px] text-foreground-600"}>
                  {works.filter((item) => item.category === category.key).length}
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="h-px w-full bg-background-300/60" />

      <div>
        <h3 className="mb-2.5 flex items-center gap-1.5 font-heading text-[11px] uppercase tracking-[0.2em] text-foreground-600">
          <i className="ri-earth-line text-primary-500" />
          制作地区
        </h3>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => onChange({ region: undefined })}
            className={`cursor-pointer whitespace-nowrap rounded-md border px-2.5 py-1.5 text-[12px] transition ${
              !region
                ? "border-primary-500 bg-primary-500 font-medium text-background-950"
                : "border-background-300/70 text-foreground-400 hover:border-primary-700/60 hover:text-primary-300"
            }`}
          >
            全部
          </button>
          {regions.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onChange({ region: item })}
              className={`cursor-pointer whitespace-nowrap rounded-md border px-2.5 py-1.5 text-[12px] transition ${
                region === item
                  ? "border-primary-500 bg-primary-500 font-medium text-background-950"
                  : "border-background-300/70 text-foreground-400 hover:border-primary-700/60 hover:text-primary-300"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-2.5 flex items-center gap-1.5 font-heading text-[11px] uppercase tracking-[0.2em] text-foreground-600">
          <i className="ri-calendar-line text-primary-500" />
          上映年份
        </h3>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => onChange({ year: undefined })}
            className={`cursor-pointer whitespace-nowrap rounded-md border px-2.5 py-1.5 text-[12px] transition ${
              !year
                ? "border-primary-500 bg-primary-500 font-medium text-background-950"
                : "border-background-300/70 text-foreground-400 hover:border-primary-700/60 hover:text-primary-300"
            }`}
          >
            全部
          </button>
          {years.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onChange({ year: String(item) })}
              className={`cursor-pointer whitespace-nowrap rounded-md border px-2.5 py-1.5 text-[12px] transition ${
                year === String(item)
                  ? "border-primary-500 bg-primary-500 font-medium text-background-950"
                  : "border-background-300/70 text-foreground-400 hover:border-primary-700/60 hover:text-primary-300"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="h-px w-full bg-background-300/60" />

      <div>
        <h3 className="mb-2.5 flex items-center gap-1.5 font-heading text-[11px] uppercase tracking-[0.2em] text-foreground-600">
          <i className="ri-sort-desc text-primary-500" />
          排序方式
        </h3>
        <div className="space-y-1">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange({ sort: option.key })}
              className={`flex w-full cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-[13px] transition ${
                sort === option.key
                  ? "bg-background-200 text-primary-300"
                  : "text-foreground-400 hover:bg-background-200"
              }`}
            >
              <span className="flex items-center gap-2">
                <i className={option.icon} />
                {option.label}
              </span>
              {sort === option.key ? <i className="ri-check-line text-primary-400" /> : null}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onReset}
        className="flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-3 py-2 text-[12px] text-foreground-400 transition hover:border-accent-600/70 hover:text-accent-300"
      >
        <i className="ri-restart-line" />
        重置全部筛选
      </button>
    </div>
  );
}